import type { Zone } from '@versesverse/core';

/*
 * THE FLIGHT IN — what a reader sees before they see anything they have to do.
 *
 * The map opens at full extent, holds long enough to register as a place rather than a diagram,
 * and then flies: to the centre where the names of God are, out to the rim where the naming stops,
 * across the three busiest neighbourhoods, and finally down onto one name, which is where the
 * reader is left standing. A short line is said over each beat.
 *
 * IT REPLACED A WALK THAT MADE THE READER EARN THIS. The arrival used to be six names opened one
 * at a time and only then the field pulling back — the payoff came ninety seconds after a reader
 * who did not yet know there was one. Flipping it costs nothing the walk was for: the fog still
 * closes in as the camera lands, so "the rest stays dark until you go there" is now something the
 * reader watches happen to a field they have just been shown, rather than a promise about names
 * they never saw.
 *
 * THE NARRATION CANNOT INVENT. A game's opening can describe a kingdom because the kingdom is
 * whatever it says; every line here has to be true of the build in front of the reader, so each is
 * either counted from what shipped or a fact about the map's own construction. That is a tighter
 * constraint and a better script: "at the centre, the names of God — every tradition puts something
 * different there" is a stronger tease than anything that could have been made up.
 *
 * NOTHING HERE TOUCHES A CANVAS. The beats are data — when, where, how close, what is said — so the
 * flight can be asserted at any millisecond without a renderer, and so the one place that decides
 * the timing is not spread across three effects that will eventually disagree.
 */

/** What a beat says. The words themselves live in the dictionary; a place's beat names the place. */
export type Narration = 'size' | 'centre' | 'rim' | 'place' | 'landing';

/** One beat of the flight. */
export interface TourStop {
  /** Milliseconds from the start. */
  at: number;
  /** Where to point the camera, in WORLD units — or null for the whole field. */
  to: { x: number; y: number } | null;
  /** How close, as a multiple of the zoom that fits the whole field. */
  zoom: number;
  /** How long the move takes. Overruns the next beat, so the camera never stops. */
  durMs: number;
  /** What is said here. */
  says: Narration;
  /** The place this beat is about, for the beats that are about one. */
  place: Zone | null;
}

/**
 * HOW MANY PLACES THE FLIGHT VISITS.
 *
 * Three, and the flight is the only surface they have — there is no grid of them any more, so the
 * count is not "how many fit in a grid" but "how many a reader will still be watching for". Each
 * beat is three and a half seconds; six would be twenty seconds of place names before the reader
 * has touched anything.
 */
export const TOUR_PLACES = 3;

/**
 * WHEN EACH BEAT LANDS — twenty-four seconds, and the whole of it skippable from the first.
 *
 * The opening HOLD is the short one and it is the most important: the field arrives at full extent
 * and sits still, which is the only moment a reader sees the whole shape of this before anything
 * starts moving. Everything after it is longer, because everything after it has a sentence to read.
 *
 * Twenty-four seconds is long for a web page and short for an opening. It is bounded by the way
 * out being on screen the entire time, so nobody is held: a reader who wants the map presses once.
 */
const CENTRE_AT = 2600;
const RIM_AT = 6200;
const PLACES_AT = 10_000;
const PLACE_GAP_MS = 3400;
const DESCENT_AT = 20_200;

/** When the flight is over and the reader is standing somewhere. */
export const TOUR_END_MS = 24_400;

/** How long a move overruns the next beat by, so the camera is retargeted while still travelling. */
const OVERRUN = 1.3;

/** How close each kind of beat sits, as a multiple of the zoom that fits the field. */
const CENTRE_ZOOM = 3.2;
const RIM_ZOOM = 1.5;
const PLACE_ZOOM = 2.4;
/**
 * And the descent.
 *
 * Deliberately short of a reading zoom rather than at it: opening the figure the flight lands on
 * runs `focusOn`, which only ever zooms IN and finishes the job exactly. A descent that overshot
 * would leave the reader inside one disc with no way to tell what happened.
 */
const DESCENT_ZOOM = 4;

/**
 * Build the flight from the places this build actually has, and the shape of the field.
 *
 * `rim` is a point out past the outermost names — where the naming stops. Taken from the map
 * rather than assumed, because how far out the field reaches is a fact about the roster.
 */
export function tourStops(places: readonly Zone[], rim: { x: number; y: number }): TourStop[] {
  const stops: TourStop[] = [
    { at: 0, to: null, zoom: 1, durMs: 0, says: 'size', place: null },
    { at: CENTRE_AT, to: { x: 0, y: 0 }, zoom: CENTRE_ZOOM, durMs: 0, says: 'centre', place: null },
    { at: RIM_AT, to: rim, zoom: RIM_ZOOM, durMs: 0, says: 'rim', place: null },
  ];

  /*
   * The places, SMALLEST FIRST, so the flight builds toward the one it lands on. `zonesFor` ranks
   * them biggest first, which is the right order for a list and the wrong one for a flight: ending
   * on the smallest would read as the camera losing interest.
   */
  const toured = places.slice(0, TOUR_PLACES);
  [...toured].reverse().forEach((place, i) => {
    stops.push({
      at: PLACES_AT + i * PLACE_GAP_MS,
      to: { x: place.x, y: place.y },
      zoom: PLACE_ZOOM,
      durMs: 0,
      says: 'place',
      place,
    });
  });

  /* And down onto the first — the busiest neighbourhood on the map, and the last place named. */
  const landing = toured[0];
  stops.push({
    at: DESCENT_AT,
    to: landing ? { x: landing.x, y: landing.y } : null,
    zoom: landing ? DESCENT_ZOOM : 1,
    durMs: 0,
    says: 'landing',
    place: landing ?? null,
  });

  return stops.map((stop, i) => ({ ...stop, durMs: overrun(stop, stops[i + 1]) }));
}

function overrun(stop: TourStop, next: TourStop | undefined): number {
  const gap = (next ? next.at : TOUR_END_MS) - stop.at;
  return Math.round(Math.max(0, gap) * OVERRUN);
}

/** The beat in force at `ms`, or null before the first. */
export function stopAt(stops: readonly TourStop[], ms: number): TourStop | null {
  let current: TourStop | null = null;
  for (const stop of stops) {
    if (stop.at > ms) break;
    current = stop;
  }
  return current;
}

/**
 * Where the flight leaves the reader — the name they are standing on when it ends.
 *
 * Null for a build with no places at all, which is a build with no ties; the flight still runs and
 * simply ends looking at the whole field.
 */
export function landingOf(places: readonly Zone[]): Zone | null {
  return places[0] ?? null;
}
